// -----------------------------------------------------------------------------
// main.ts — application entry point.
//
// Flow (CLAUDE.md §4/§5):
//   1. Run the capability gate (detect what the browser can do).
//   2. Derive the runtime config (preview proxy, export path, storage backend).
//   3. If "blocked", show the gate screen and wait for the user's "Try anyway".
//   4. Mount the editor (which shows a degraded banner when relevant).
//
// Everything from here on is 100% client-side — no network, no uploads.
// -----------------------------------------------------------------------------
import './ui/styles.css';
import { deriveRuntimeConfig } from './preflight/capabilities';
import { detectCapabilities } from './preflight/detect';
import { showBlockedGate } from './preflight/gate-screen';
import { mountEditor } from './ui/app';

// Last-resort safety net: surface any uncaught error or rejected promise to the
// user as a toast instead of letting the app fail silently (CLAUDE.md §2). This
// is installed before boot so it also covers startup failures.
function installCrashGuard(): void {
  let toast: HTMLElement | null = null;
  const show = (message: string): void => {
    if (!toast) {
      toast = document.createElement('div');
      toast.setAttribute(
        'style',
        'position:fixed;left:14px;bottom:14px;z-index:9999;max-width:60vw;' +
          'background:#1a1714;border:1px solid #ff4d4f;color:#ffd2cc;border-radius:8px;' +
          'padding:8px 12px;font:12.5px system-ui,sans-serif',
      );
      const close = document.createElement('button');
      close.textContent = '✕';
      close.setAttribute('style', 'background:none;border:none;color:#a39c8d;margin-left:10px;cursor:pointer');
      close.addEventListener('click', () => toast?.remove());
      const text = document.createElement('span');
      text.className = 'zf-crash-text';
      toast.append(text, close);
      document.body.append(toast);
    }
    const text = toast.querySelector('.zf-crash-text');
    if (text) text.textContent = 'Something went wrong: ' + message; // textContent — never innerHTML
  };
  window.addEventListener('error', (e) => show(e.message || 'unknown error'));
  window.addEventListener('unhandledrejection', (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    show(reason instanceof Error ? reason.message : String(reason));
  });
}

async function boot(): Promise<void> {
  const root = document.getElementById('app');
  if (!root) return;

  let caps;
  try {
    caps = await detectCapabilities();
  } catch (err) {
    // If detection itself fails, we still tell the user — never a blank screen.
    root.className = '';
    root.textContent = 'ZeroFrame failed to start: ' + (err instanceof Error ? err.message : String(err));
    return;
  }

  const config = deriveRuntimeConfig(caps);

  if (caps.tier === 'blocked') {
    await showBlockedGate(root, caps); // resolves when the user overrides
  }

  mountEditor(root, caps, config);
}

installCrashGuard();
void boot();
